// ./src/utils/api/ServiceIntervalNextService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { Car } from "./CarService";
import { ExpenseKind } from "./AppEssentialsService";

// =============================================================================
// GraphQL Fragments
// =============================================================================

const SERVICE_INTERVAL_NEXT_FIELDS = `
  id
  carId
  kindId
  intervalType
  mileageInterval
  daysInterval
  maxWhenDone
  maxOdometer
  nextWhenDo
  nextOdometer
  remainingDays
  remainingMileage
  urgencyStatus
  status
  car {
    id
    label
    model
    manufacturedIn
    vehicleMake {
      id
      makeName
    }
  }
  expenseKind {
    id
    code
    name
    canSchedule
    isItMaintenance
  }
`;

// =============================================================================
// GraphQL Queries
// =============================================================================

const SERVICE_INTERVAL_NEXT_LIST_QUERY = gql`
  query ServiceIntervalNextList(
    $filter: ServiceIntervalNextFilter
    $params: PaginationAndSorting
  ) {
    serviceIntervalNextList(filter: $filter, params: $params) {
      code
      data {
        ${SERVICE_INTERVAL_NEXT_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const SERVICE_INTERVAL_NEXT_GET_QUERY = gql`
  query ServiceIntervalNextGet($id: ID) {
    serviceIntervalNextGet(id: $id) {
      code
      data {
        ${SERVICE_INTERVAL_NEXT_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// GraphQL Mutations
// =============================================================================

const SERVICE_INTERVAL_NEXT_UPDATE_MUTATION = gql`
  mutation ServiceIntervalNextUpdate(
    $where: ServiceIntervalNextWhereInput!
    $params: ServiceIntervalNextInput!
  ) {
    serviceIntervalNextUpdate(where: $where, params: $params) {
      code
      data {
        ${SERVICE_INTERVAL_NEXT_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// Types
// =============================================================================

/**
 * Urgency status for upcoming services
 */
export type UrgencyStatus = "overdue" | "due_soon" | "upcoming" | "ok";

/**
 * Service interval next record - represents an upcoming scheduled service
 */
export interface ServiceIntervalNext {
  id?: string;
  carId?: string;
  kindId?: number;
  intervalType?: number;
  mileageInterval?: number;
  daysInterval?: number;
  maxWhenDone?: string;
  maxOdometer?: number;
  nextWhenDo?: string;
  nextOdometer?: number;
  remainingDays?: number;
  remainingMileage?: number;
  urgencyStatus?: UrgencyStatus;
  status?: number;
  car?: Car;
  expenseKind?: ExpenseKind;
}

/**
 * Input for updating a service interval next record (user override)
 */
export interface ServiceIntervalNextInput {
  nextWhenDo?: string | null;
  nextOdometer?: number | null;
}

/**
 * Filter for listing service interval next records
 */
export interface ServiceIntervalNextFilter {
  carId?: string[] | null;
  kindId?: number[] | null;
  intervalType?: number[] | null;
  urgencyStatus?: UrgencyStatus[] | null;
}

/**
 * Where input for identifying a specific record
 */
export interface ServiceIntervalNextWhereInput {
  id: string;
}

// =============================================================================
// Service Class
// =============================================================================

/**
 * ServiceIntervalNextService handles API calls for upcoming scheduled services.
 *
 * These records are automatically created/updated when expenses with scheduleable
 * kindIds are added. The service provides:
 * - `list`: Get all upcoming services, optionally filtered by car, kind, or urgency
 * - `get`: Get a single service record by ID
 * - `update`: Override the calculated next due date/mileage
 */
export class ServiceIntervalNextService {
  /**
   * Get a list of upcoming service intervals.
   * Returns services sorted by urgency and due date.
   *
   * @param filter - Optional filters (carId, kindId, intervalType, urgencyStatus)
   * @param pagination - Optional pagination params
   * @param sortBy - Optional sort configuration
   */
  static async list(
    filter?: ServiceIntervalNextFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(
      SERVICE_INTERVAL_NEXT_LIST_QUERY,
      {
        filter: filter || {},
        params: {
          pagination: pagination || undefined,
          sortBy: sortBy || undefined,
        },
      }
    );
    return result;
  }

  /**
   * Get a single service interval by ID.
   *
   * @param id - The service interval ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(
      SERVICE_INTERVAL_NEXT_GET_QUERY,
      { id }
    );
    return result;
  }

  /**
   * Update a service interval (user override of next due date/mileage).
   * This allows users to manually adjust when a service is next due.
   *
   * @param id - The service interval ID to update
   * @param params - The override values (nextWhenDo and/or nextOdometer)
   */
  static async update(
    id: string,
    params: ServiceIntervalNextInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      SERVICE_INTERVAL_NEXT_UPDATE_MUTATION,
      {
        where: { id },
        params,
      }
    );
    return result;
  }
}